import React from 'react';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const SpendingSummaryCard = ({ data }) => {
  const budgetPercentage = (data?.totalSpent / data?.budgetLimit) * 100;
  const remaining = data?.budgetLimit - data?.totalSpent;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Monthly Spending Summary</h2>
          <p className="card-description">Overview of your spending this month</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 rounded-lg">
          <Icon name="TrendingDown" size={16} className="text-success" />
          <span className="text-sm font-semibold text-success">{Math.abs(data?.change)}% less</span>
        </div>
      </div>
      <div className="card-content">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col justify-center">
            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl font-bold text-foreground">${data?.totalSpent?.toLocaleString()}</span>
                <span className="text-lg text-muted-foreground">/ ${data?.budgetLimit?.toLocaleString()}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 mb-2">
                <div 
                  className={`h-3 rounded-full transition-all ${
                    budgetPercentage >= 90 ? 'bg-error' : 
                    budgetPercentage >= 75 ? 'bg-warning': 'bg-success'
                  }`}
                  style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                ${remaining?.toFixed(2)} remaining ({(100 - budgetPercentage)?.toFixed(1)}% of budget)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Receipt" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Transactions</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{data?.transactions}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Top Category</span>
                </div>
                <p className="text-lg font-bold text-foreground">{data?.topCategory}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data?.breakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="percentage"
                >
                  {data?.breakdown?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload?.length) {
                      const data = payload?.[0]?.payload;
                      return (
                        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                          <p className="text-sm font-semibold text-foreground mb-1">{data?.category}</p>
                          <p className="text-lg font-bold" style={{ color: data?.color }}>
                            ${data?.amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">{data?.percentage}% of total</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {data?.breakdown?.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: item?.color }}
                />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{item?.category}</p>
                  <p className="text-sm font-semibold text-foreground">${item?.amount?.toFixed(0)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpendingSummaryCard;